import { createFileRoute, useSearch } from '@tanstack/react-router'
import { useMemo } from 'react'
import { useQuery } from '@connectrpc/connect-query'
import { create } from '@bufbuild/protobuf'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { AlertTriangle, FileQuestion } from 'lucide-react'
import { getInquiryOrgInfo } from '@/gen/cortado/v1/cortado-CortadoService_connectquery'
import { GetInquiryOrgInfoRequestSchema } from '@/gen/cortado/v1/cortado_pb'
import { ResendLinkCard } from '@/components/resend-link-card'

type ExpiredSearchParams = {
  token?: string
}

export const Route = createFileRoute('/lead/expired')({
  component: ExpiredTokenPage,
  validateSearch: (search: Record<string, unknown>): ExpiredSearchParams => ({
    token: typeof search.token === 'string' ? search.token : undefined,
  }),
})

function ExpiredTokenPage() {
  const { token } = useSearch({ from: '/lead/expired' })

  const request = useMemo(
    () => create(GetInquiryOrgInfoRequestSchema, { token: token || '' }),
    [token],
  )
  const { data: orgInfo } = useQuery(getInquiryOrgInfo, request, {
    enabled: !!token,
  })

  const orgName = orgInfo?.orgName
  const orgLogoUrl = orgInfo?.orgLogoUrl

  return (
    <div className="min-h-svh bg-background flex items-center justify-center p-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center space-y-4">
          {orgLogoUrl ? (
            <img
              src={orgLogoUrl}
              alt={orgName || 'Organization'}
              className="h-20 w-20 mx-auto rounded-lg object-cover"
            />
          ) : (
            <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-muted">
              <FileQuestion className="h-10 w-10 text-muted-foreground" />
            </div>
          )}
          {orgName && (
            <div>
              <h1 className="display-heading text-3xl font-semibold tracking-tight text-foreground">
                {orgName}
              </h1>
            </div>
          )}
        </div>

        {/* Error Card */}
        <Card className="border-destructive">
          <CardHeader>
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-6 w-6 text-destructive" />
              <div>
                <CardTitle className="text-destructive">Link no longer valid</CardTitle>
                <CardDescription>Please ask for a new one</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              {orgName
                ? <>This link has expired or is no longer valid. Please reply to your email or contact <strong>{orgName}</strong> and ask them to send you a new link — they can resend your quote in seconds.</>
                : 'This link has expired or is no longer valid. Please reply to your email or contact the business that sent it and ask them to send you a new link.'}
            </p>
          </CardContent>
        </Card>

        <ResendLinkCard token={token} />

        {/* Additional Help Text */}
        <p className="text-center text-xs text-dim">
          {orgName
            ? `Need help? Contact ${orgName} directly.`
            : 'Need help? Contact the business you inquired with.'}
        </p>
      </div>
    </div>
  )
}
